import type { TouchIconName } from "./icons.ts";
import type { InteractView } from "./interact-view.ts";

/**
 * The touch action buttons (ADR-061's amendment of 2026-10-31, decision 7), one registry: each button's place and size
 * by the plan's mockup, and the handler it calls. A handler is the page's own function for the action, the one its key
 * calls too, so a key, a tap and the prompt cannot disagree.
 */

/**
 * A button's id.
 */
export type TouchActionId = "attack" | "interact" | "jump" | "sprint" | "stance" | "tool";

/**
 * What a button does when tapped, by id.
 */
export type TouchHandlers = Readonly<Record<TouchActionId, () => void>>;

/**
 * What the buttons draw that the page changes: the Interact button's view, whether Sprint is latched on, whether the
 * weapon is drawn, and whether a tool is in hand.
 */
export interface TouchView {
  readonly interact: InteractView;
  readonly sprint: boolean;
  readonly drawn: boolean;
  readonly toolInHand: boolean;
}

/**
 * Where a button sits and how it looks: a size in logical pixels (at least 32), its distance from the cluster's right and
 * bottom edges, whether it is a primary (brass) button, and its fixed icon, if any.
 */
export interface TouchButtonSpec {
  readonly id: TouchActionId;
  readonly size: number;
  readonly right: number;
  readonly bottom: number;
  readonly primary: boolean;
  readonly icon: TouchIconName | null;
}

/**
 * The buttons, right thumb: Attack and Interact on the bottom row with Tool, Jump above Attack with Sprint and Stance
 * beside it. The two primary buttons are 48 and 44 and the others 40, so the 32 pixel icon fills the face.
 */
export const TOUCH_BUTTONS: readonly TouchButtonSpec[] = [
  { id: "attack", size: 48, right: 4, bottom: 4, primary: true, icon: "attack" },
  { id: "interact", size: 44, right: 56, bottom: 4, primary: true, icon: null },
  { id: "tool", size: 40, right: 104, bottom: 4, primary: false, icon: "tool" },
  { id: "jump", size: 40, right: 8, bottom: 56, primary: false, icon: "jump" },
  { id: "sprint", size: 40, right: 52, bottom: 56, primary: false, icon: "sprint" },
  { id: "stance", size: 40, right: 96, bottom: 56, primary: false, icon: null },
];

/**
 * The room the cluster takes from the lower right corner: the farthest button's reach and a margin, in logical pixels.
 */
export const TOUCH_EXTENT = {
  width: Math.max(...TOUCH_BUTTONS.map((button) => button.right + button.size)) + 4,
  height: Math.max(...TOUCH_BUTTONS.map((button) => button.bottom + button.size)) + 4,
} as const;

/**
 * The smallest a button may be, in logical pixels.
 */
export const TOUCH_MIN_SIZE = 32;

/**
 * Taps a button: calls its handler.
 *
 * @param handlers The handlers.
 * @param id The button.
 */
export function tapTouchButton(handlers: TouchHandlers, id: TouchActionId): void {
  handlers[id]();
}
